import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Settings, User, Users, List, Bell, ArrowLeft, CreditCard } from 'lucide-react';
import { Loader, Tabs } from '@mantine/core';
import { SettingsSummaryDto, SettingsSection } from '../types/settings';
import { getSettingsSummary } from '../api/settingsApi';
import { NotificationSettings } from './settings/NotificationSettings';
import { ContactManagement } from './settings/ContactManagement';
import { ListManagement } from './settings/ListManagement';
import { GeneralSettings } from './settings/GeneralSettings';
import { PlanPage } from './settings/PlanPage';
import PushNotificationSetup from './PushNotificationSetup';
import { trackPageVisit } from '../utils/pageVisitTracker';
import './Settings.css';


export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Read initial section from URL parameters or navigation state, fallback to 'general'
  const getInitialSection = (): SettingsSection => {
    const tabParam = searchParams.get('tab') as SettingsSection;
    if (tabParam && ['general', 'notifications', 'contacts', 'lists', 'plan'].includes(tabParam)) {
      return tabParam;
    }
    
    return (typeof window !== 'undefined' && window.history && window.history.state && window.history.state.usr && window.history.state.usr.section) 
      ? window.history.state.usr.section 
      : 'general';
  };
  
  const [activeSection, setActiveSection] = useState<SettingsSection>(getInitialSection());
  const [settingsData, setSettingsData] = useState<SettingsSummaryDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettingsData();
  }, []);

  // Track visits to subscription plan section for onboarding completion
  useEffect(() => {
    if (activeSection === 'plan') {
      trackPageVisit.subscriptionPlan();
    }
  }, [activeSection]);

  const loadSettingsData = async () => {
    try {
      setIsLoading(true);
      const data = await getSettingsSummary();
      setSettingsData(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = () => {
    loadSettingsData();
  };

  const tabItems = [
    {
      id: 'general' as SettingsSection,
      label: 'General',
      icon: User,
      description: 'Theme, language, and preferences'
    },
    {
      id: 'plan' as SettingsSection,
      label: 'Subscription Plan',
      icon: CreditCard,
      description: 'Manage your premium subscription'
    },
    {
      id: 'notifications' as SettingsSection,
      label: 'Notifications',
      icon: Bell,
      description: 'Email and in-app notification settings'
    },
    {
      id: 'contacts' as SettingsSection,
      label: 'Contacts & Invitations',
      icon: Users,
      description: 'Manage contacts and invitations'
    },
    {
      id: 'lists' as SettingsSection,
      label: 'List Management',
      icon: List,
      description: 'Configure your todo lists'
    }
  ];


  if (isLoading) {
    return (
      <div className="settings-page">
        <div className="settings-header">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            Back to Todo List
          </button>
          <h1><Settings size={24} /> Settings</h1>
        </div>
        <div className="settings-loading">
          <Loader type="bars" size="md" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="settings-page">
        <div className="settings-header">
          <button onClick={() => navigate('/')} className="back-button">
            <ArrowLeft size={20} />
            Back to Todo List
          </button>
          <h1><Settings size={24} /> Settings</h1>
        </div>
        <div className="settings-error">
          <p>Error loading settings: {error}</p>
          <button onClick={loadSettingsData} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <button onClick={() => navigate('/')} className="back-button">
          <ArrowLeft size={20} />
          Back to Todo List
        </button>
        <h1><Settings size={24} /> Settings</h1>
      </div>

      <div className="settings-content">
        <Tabs 
          value={activeSection} 
          onChange={(value) => setActiveSection(value as SettingsSection)}
          orientation="horizontal"
          variant="default"
          styles={{
            root: {
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            },
            list: {
              backgroundColor: 'var(--app-surface)',
              borderBottom: '1px solid var(--app-border)',
              padding: '0 24px',
              flexShrink: 0
            },
            tab: {
              color: 'var(--app-text-secondary)',
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              '&[data-active]': {
                color: 'var(--mantine-color-blue-6)',
                borderBottomColor: 'var(--mantine-color-blue-6)'
              },
              '&:hover': {
                backgroundColor: 'var(--app-bg)',
                color: 'var(--app-text-primary)'
              }
            },
            panel: {
              flex: 1,
              overflow: 'auto',
              padding: '24px',
              paddingBottom: '100px' // Extra space for save buttons
            }
          }}
        >
          <Tabs.List>
            {tabItems.map((item) => {
              const Icon = item.icon;
              return (
                <Tabs.Tab 
                  key={item.id} 
                  value={item.id}
                  leftSection={<Icon size={18} />}
                >
                  {item.label}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          <Tabs.Panel value="general">
            {settingsData && (
              <GeneralSettings 
                userSettings={settingsData.userSettings}
                onSettingsUpdate={refreshData}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="plan">
            <PlanPage />
          </Tabs.Panel>

          <Tabs.Panel value="notifications">
            {settingsData && (
              <div>
                <PushNotificationSetup />
                <div style={{ marginTop: '2rem' }}>
                  <NotificationSettings 
                    userSettings={settingsData.userSettings}
                    onSettingsUpdate={refreshData}
                  />
                </div>
              </div>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="contacts">
            {settingsData && (
              <ContactManagement 
                contacts={settingsData.contacts}
                sentInvitations={settingsData.sentInvitations}
                receivedInvitations={settingsData.receivedInvitations}
                onDataUpdate={refreshData}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="lists">
            {settingsData && (
              <ListManagement 
                lists={settingsData.manageableLists}
                onDataUpdate={refreshData}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </div>
    </div>
  );
};
